import { createContext, useContext, useLayoutEffect, useState, useCallback, ReactNode, useRef } from "react";

// יצירת קונטקסט עם ערך ברירת מחדל
export const ScreenReaderContext = createContext({
  isBusy: false,
  stopReading: () => {},
  setElementId: (id: string) => {},
});

export const ScreenReaderProvider = ({ children }: { children: ReactNode }) => {
  const [elementId, setElementId] = useState<string | null>(null); // ✅ ניתן לשינוי
  const [isBusy, setIsBusy] = useState(false); // ✅ מחליף את useRef כדי לגרום לרינדור מחדש
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousTextRef = useRef<string | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);


  useLayoutEffect(() => {
    if (!elementId) return;

    // log(`🔍 ממתין לטעינת האלמנט עם id=${elementId}...`);

    const element = document.getElementById(elementId);
    if (!element) {
      // log(`❌ אלמנט עם id=${elementId} לא נמצא. ננסה שוב בעוד 500ms.`);
      setTimeout(() => {}, 500); // ניסיון נוסף אם האלמנט נטען מאוחר
      return;
    }

    // log(`✅ האלמנט נמצא: id=${elementId}, תוכן התחלתי="${element.innerText}"`);
    previousTextRef.current = element.innerText;

    if (observerRef.current) {
      // log("⚠️ `MutationObserver` כבר קיים, לא יוצרים מחדש.");
      return;
    }

    // log("🟢 מגדיר `MutationObserver`...");
    observerRef.current = new MutationObserver((mutationsList) => {
      // log("🟢 `MutationObserver` הופעל, בודק שינויים...");
      mutationsList.forEach((mutation) => {
        // log(`🔍 שינוי זוהה: ${mutation.type}`);

        if (mutation.type === "childList" || mutation.type === "characterData") {
          const currentText = element.textContent?.trim() || "";
          // log(`🧐 טקסט קודם: "${previousTextRef.current}", טקסט נוכחי: "${currentText}"`);

          if (previousTextRef.current !== currentText) {
            previousTextRef.current = currentText;
            setIsBusy(true); // ✅ מעדכן את ה-UI
            // log(`📢 קורא המסך התחיל להקריא → aria-busy=true`);
            resetTimeout();
          }
        }
      });
    });

    observerRef.current.observe(element, { childList: true, subtree: true, characterData: true });

    const handleFocusChange = () => {
      // log(`➡️ המשתמש שינה פוקוס, ממתין 5 שניות לפני ביטול aria-busy`);
      resetTimeout();
    };

    document.addEventListener("focusin", handleFocusChange);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // log(`🚨 המשתמש עזב את הדף → הפסקת aria-busy מידית`);
        stopReading();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const handleBeforeUnload = () => {
      // log(`🔄 המשתמש טוען מחדש את הדף → הפסקת aria-busy מידית`);
      stopReading();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // log("🔄 ניקוי ניטור האלמנט");
      observerRef.current?.disconnect();
      observerRef.current = null;
      document.removeEventListener("focusin", handleFocusChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [elementId]);

  const resetTimeout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // log(`⏳ טיימר הפסקת הקראה יתחיל (5 שניות)`);
    timeoutRef.current = setTimeout(() => {
      // log(`✅ סיום ההקראה → aria-busy=false`);
      setIsBusy(false); // ✅ מעדכן את ה-UI
    }, 5000);
  };

  const stopReading = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // log(`🛑 הפסקת ההקראה ידנית → aria-busy=false`);
    setIsBusy(false); // ✅ מעדכן את ה-UI
  }, []);

  return (
    <ScreenReaderContext.Provider value={{ isBusy, stopReading, setElementId }}>
      {children}
    </ScreenReaderContext.Provider>
  );
};

export const useScreenReaderStatus = () => {
  return useContext(ScreenReaderContext);
};
